import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Fingerprint, Users, ChevronLeft, ChevronRight, Pencil, Download, RotateCcw, Check, Trash2, ListChecks, Filter, Target } from "lucide-react";
import { PLAYERS, SIGNATURES } from "@/players";
import { IMAGES } from "@/assets";

// IMPORTANT: use LANDSCAPE image consistently (1080 x 810). Do NOT mix with portrait IMAGES[3].
const MAP_IMAGE = IMAGES[0];

const uiCopy = {
  it: {
    eyebrow: "Mappa interattiva",
    title: "Ogni volto, ogni firma.",
    lede: "Tocca un giocatore per rivelarne ruolo, statistiche e memoria storica. Passa alle firme per esplorare gli autografi originali.",
    tabFaces: "Volti",
    tabSigs: "Firme",
    tabEdit: "Modifica",
    hint: "Tocca un cerchio per scoprire",
    editHint: "Clicca sul volto di un giocatore per registrarne la posizione",
    close: "Chiudi",
    role: "Ruolo",
    season: "Stagione 1982 / 83",
    fact: "Il ricordo",
    prev: "Precedente",
    next: "Successivo",
    keyHint: "← → naviga · ESC chiudi",
    assign: "Assegna posizione a",
    assignPick: "Scegli un nome",
    confirm: "Conferma",
    cancel: "Annulla",
    reset: "Reset",
    resetAll: "Reset tutti",
    export: "Esporta JSON",
    exported: "Coordinate copiate negli appunti",
    editing: "Modalità modifica attiva",
    edited: "modificati",
    progress: "Progresso calibrazione",
    completed: "completati",
    filterAll: "Tutti",
    filterPending: "Da calibrare",
    filterDone: "Calibrati",
    focus: "Individua",
    focused: "Evidenziato",
    backRow: "Fila in alto",
    middleRow: "Fila centrale",
    frontRow: "Fila seduta",
  },
  en: {
    eyebrow: "Interactive map",
    title: "Every face, every signature.",
    lede: "Tap a player to reveal role, season stats and historical memory. Switch to signatures to explore the original autographs.",
    tabFaces: "Faces",
    tabSigs: "Signatures",
    tabEdit: "Edit",
    hint: "Tap a circle to reveal",
    editHint: "Click a player's face to record its position",
    close: "Close",
    role: "Role",
    season: "1982 / 83 Season",
    fact: "The memory",
    prev: "Previous",
    next: "Next",
    keyHint: "← → navigate · ESC close",
    assign: "Assign position to",
    assignPick: "Pick a name",
    confirm: "Confirm",
    cancel: "Cancel",
    reset: "Reset",
    resetAll: "Reset all",
    export: "Export JSON",
    exported: "Coordinates copied to clipboard",
    editing: "Edit mode active",
    edited: "edited",
    progress: "Calibration progress",
    completed: "completed",
    filterAll: "All",
    filterPending: "Pending",
    filterDone: "Calibrated",
    focus: "Locate",
    focused: "Highlighted",
    backRow: "Back row",
    middleRow: "Middle row",
    frontRow: "Front row",
  },
};

const STORAGE_KEY = "asroma_face_coords_v1";

const InteractivePhoto = ({ lang }) => {
  const t = uiCopy[lang] || uiCopy.en;
  const [mode, setMode] = useState("faces"); // "faces" | "signatures" | "edit"
  const [selected, setSelected] = useState(null);
  const [customCoords, setCustomCoords] = useState({});
  const [pendingClick, setPendingClick] = useState(null); // {x, y}
  const [pickedPlayer, setPickedPlayer] = useState("");
  const [toast, setToast] = useState(null);
  const [focusedId, setFocusedId] = useState(null); // pulsed spotlight in edit mode
  const [panelFilter, setPanelFilter] = useState("all"); // "all" | "pending" | "done"
  const imgWrapRef = useRef(null);

  // Load persisted custom coords
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setCustomCoords(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  // Effective players with any custom overrides applied
  const effectivePlayers = useMemo(
    () => PLAYERS.map((p) => customCoords[p.id] ? { ...p, face: customCoords[p.id] } : p),
    [customCoords]
  );

  const selectedIndex = useMemo(
    () => (selected ? effectivePlayers.findIndex((p) => p.id === selected) : -1),
    [selected, effectivePlayers]
  );
  const player = selectedIndex >= 0 ? effectivePlayers[selectedIndex] : null;

  const goPrev = () => {
    if (selectedIndex < 0) return;
    const next = (selectedIndex - 1 + effectivePlayers.length) % effectivePlayers.length;
    setSelected(effectivePlayers[next].id);
  };
  const goNext = () => {
    if (selectedIndex < 0) return;
    const next = (selectedIndex + 1) % effectivePlayers.length;
    setSelected(effectivePlayers[next].id);
  };

  useEffect(() => {
    if (!player) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSelected(null);
      else if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [player]);

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2200);
  };

  const persist = (next) => {
    setCustomCoords(next);
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const handleImageClick = useCallback((e) => {
    if (mode !== "edit") return;
    const rect = imgWrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingClick({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
    setPickedPlayer("");
  }, [mode]);

  const confirmAssign = () => {
    if (!pendingClick || !pickedPlayer) return;
    persist({ ...customCoords, [pickedPlayer]: pendingClick });
    setPendingClick(null);
    setPickedPlayer("");
    const p = PLAYERS.find((pl) => pl.id === pickedPlayer);
    showToast(`✓ ${p?.name} → ${pendingClick.x}%, ${pendingClick.y}%`);
  };

  const resetOne = (id) => {
    const { [id]: _, ...rest } = customCoords;
    persist(rest);
  };
  const resetAll = () => persist({});

  const exportJSON = () => {
    const payload = effectivePlayers.map((p) => ({ id: p.id, name: p.name, face: p.face }));
    const json = JSON.stringify(payload, null, 2);
    navigator.clipboard?.writeText(json).catch(() => {});
    showToast(t.exported);
  };

  const editedCount = Object.keys(customCoords).length;
  const pendingPlayers = PLAYERS.filter((p) => !customCoords[p.id]);
  const donePlayers = PLAYERS.filter((p) => customCoords[p.id]);
  const progressPct = Math.round((editedCount / PLAYERS.length) * 100);

  const focusPlayer = (id) => {
    setFocusedId(id);
    window.clearTimeout(focusPlayer._t);
    focusPlayer._t = window.setTimeout(() => setFocusedId(null), 2400);
    imgWrapRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const filteredPanel =
    panelFilter === "pending" ? pendingPlayers
    : panelFilter === "done" ? donePlayers
    : PLAYERS;

  const rowLabel = (row) => row === "back" ? t.backRow : row === "middle" ? t.middleRow : t.frontRow;

  return (
    <section id="interactive" className="py-24 md:py-40 bg-ink text-paper relative overflow-hidden" data-testid="interactive-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-3 font-mono-tiny text-gold">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              {t.eyebrow}
            </motion.div>
          </div>
          <div className="md:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95]"
            >
              {t.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-6 text-paper/70 max-w-xl text-base md:text-lg leading-relaxed"
            >
              {t.lede}
            </motion.p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            onClick={() => setMode("faces")}
            data-testid="tab-faces"
            className={`group flex items-center gap-2 px-5 py-2.5 rounded-full font-mono-tiny transition-colors border ${
              mode === "faces" ? "bg-gold text-ink border-gold" : "text-paper/70 border-paper/20 hover:border-gold hover:text-gold"
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            {t.tabFaces}
          </button>
          <button
            onClick={() => setMode("signatures")}
            data-testid="tab-signatures"
            className={`group flex items-center gap-2 px-5 py-2.5 rounded-full font-mono-tiny transition-colors border ${
              mode === "signatures" ? "bg-gold text-ink border-gold" : "text-paper/70 border-paper/20 hover:border-gold hover:text-gold"
            }`}
          >
            <Fingerprint className="w-3.5 h-3.5" />
            {t.tabSigs}
          </button>
          <button
            onClick={() => setMode("edit")}
            data-testid="tab-edit"
            className={`group flex items-center gap-2 px-5 py-2.5 rounded-full font-mono-tiny transition-colors border ${
              mode === "edit" ? "bg-bordeaux text-paper border-bordeaux" : "text-paper/70 border-paper/20 hover:border-bordeaux hover:text-bordeaux"
            }`}
          >
            <Pencil className="w-3.5 h-3.5" />
            {t.tabEdit}
            {editedCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-gold text-ink rounded-full text-[9px] leading-none" data-testid="edit-count-badge">{editedCount}</span>
            )}
          </button>
          <div className="ml-auto font-mono-tiny text-paper/40 hidden md:block">
            {mode === "edit" ? t.editHint : t.hint}
          </div>
        </div>

        {/* Photo with hotspots — LANDSCAPE */}
        <div
          ref={imgWrapRef}
          className={`relative rounded-sm overflow-hidden bg-paper2/10 border ${mode === "edit" ? "border-bordeaux ring-2 ring-bordeaux/40" : "border-paper/10"} mx-auto ${mode === "edit" ? "cursor-crosshair" : ""}`}
          style={{ maxWidth: "1080px" }}
          onClick={handleImageClick}
          data-testid="interactive-image-wrap"
        >
          <img
            src={MAP_IMAGE}
            alt="AS Roma 1982/83 team photograph interactive map"
            className="block w-full h-auto select-none pointer-events-none"
            draggable={false}
            data-testid="interactive-image"
          />

          {/* Face hotspots */}
          <AnimatePresence>
            {(mode === "faces" || mode === "edit") && (
              <motion.div
                key="faces"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0"
              >
                {effectivePlayers.map((p) => (
                  <div
                    key={p.id}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                    style={{ left: `${p.face.x}%`, top: `${p.face.y}%`, pointerEvents: mode === "edit" ? "none" : "auto" }}
                  >
                    {/* focused-in-panel spotlight */}
                    {focusedId === p.id && (
                      <motion.span
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: [0, 2.6, 2.6], opacity: [0, 1, 0] }}
                        transition={{ duration: 2.2, ease: "easeOut" }}
                        className="absolute inset-0 rounded-full border-2 border-gold pointer-events-none"
                      />
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); if (mode !== "edit") setSelected(p.id); }}
                      data-testid={`face-hotspot-${p.id}`}
                      className="block"
                      aria-label={p.name}
                      style={{ pointerEvents: mode === "edit" ? "none" : "auto" }}
                    >
                      <span className="relative block w-6 h-6 md:w-7 md:h-7">
                        <motion.span
                          className={`absolute inset-0 rounded-full border ${customCoords[p.id] ? "border-bordeaux/80" : "border-gold/70"}`}
                          animate={{ scale: [1, 1.5, 1], opacity: [0.7, 0, 0.7] }}
                          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                        />
                        <span className={`absolute inset-0 rounded-full border ${customCoords[p.id] ? "border-bordeaux bg-bordeaux/25" : "border-gold/80 bg-gold/10"} group-hover:bg-gold/30 transition-colors`} />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className={`w-1.5 h-1.5 rounded-full ${customCoords[p.id] ? "bg-bordeaux" : "bg-gold"}`} />
                        </span>
                      </span>
                      <span className="absolute left-1/2 top-full mt-2 -translate-x-1/2 px-2 py-1 bg-ink/90 border border-gold/40 text-gold font-mono-tiny whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        {p.name}
                      </span>
                    </button>
                    {mode === "edit" && customCoords[p.id] && (
                      <button
                        onClick={(e) => { e.stopPropagation(); resetOne(p.id); }}
                        data-testid={`face-reset-${p.id}`}
                        title={t.reset}
                        className="absolute -right-4 -top-4 w-5 h-5 rounded-full bg-bordeaux text-paper flex items-center justify-center hover:bg-bordeaux-deep transition-colors"
                        style={{ pointerEvents: "auto" }}
                      >
                        <RotateCcw className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Signature hotspots */}
          <AnimatePresence>
            {mode === "signatures" && (
              <motion.div
                key="sigs"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0"
              >
                {SIGNATURES.map((s, i) => {
                  const p = effectivePlayers.find((x) => x.id === s.playerId);
                  if (!p) return null;
                  const label = s.label || p.name.split(" ").slice(-1)[0];
                  return (
                    <button
                      key={s.id}
                      onClick={(e) => { e.stopPropagation(); setSelected(p.id); }}
                      data-testid={`sig-hotspot-${s.id}`}
                      className="absolute -translate-x-1/2 -translate-y-1/2 group hover:z-20 focus:z-20"
                      style={{ left: `${s.pos.x}%`, top: `${s.pos.y}%`, zIndex: i + 1 }}
                      aria-label={p.name + " signature"}
                    >
                      <span className="relative block">
                        <motion.span
                          className="absolute -inset-3 rounded-full border border-bordeaux/60"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.7, 0, 0.7] }}
                          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        />
                        <span className="relative flex items-center gap-1.5 px-2.5 py-1 bg-bordeaux/85 border border-gold text-gold font-mono-tiny whitespace-nowrap group-hover:bg-bordeaux transition-colors">
                          <Fingerprint className="w-3 h-3" />
                          {label}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pending click marker in edit mode */}
          {mode === "edit" && pendingClick && (
            <div
              className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none z-30"
              style={{ left: `${pendingClick.x}%`, top: `${pendingClick.y}%` }}
              data-testid="pending-click-marker"
            >
              <motion.span
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                className="block w-10 h-10 rounded-full border-2 border-gold bg-gold/20"
              />
              <span className="absolute left-1/2 top-full mt-2 -translate-x-1/2 px-2 py-0.5 bg-gold text-ink font-mono-tiny whitespace-nowrap">
                {pendingClick.x}%, {pendingClick.y}%
              </span>
            </div>
          )}

          {/* Edit mode status pill */}
          {mode === "edit" && (
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-bordeaux text-paper font-mono-tiny flex items-center gap-2 pointer-events-none z-40" data-testid="edit-status">
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
              {t.editing}
            </div>
          )}
        </div>

        {/* Edit-mode toolbar under image */}
        {mode === "edit" && (
          <>
          <div className="mt-6 flex flex-wrap items-center gap-3 p-4 bg-paper2/5 border border-paper/10 rounded-sm" data-testid="edit-toolbar">
            <div className="font-mono-tiny text-paper/60">
              {editedCount} / {PLAYERS.length} {t.edited}
            </div>
            {/* progress bar */}
            <div className="flex-1 h-1 bg-paper/10 rounded-full overflow-hidden max-w-md" data-testid="edit-progress">
              <motion.div
                initial={false}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="h-full bg-gold"
              />
            </div>
            <button
              onClick={resetAll}
              disabled={editedCount === 0}
              data-testid="edit-reset-all"
              className="flex items-center gap-2 px-4 py-2 font-mono-tiny border border-paper/20 text-paper/70 hover:border-bordeaux hover:text-bordeaux transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t.resetAll}
            </button>
            <button
              onClick={exportJSON}
              data-testid="edit-export"
              className="flex items-center gap-2 px-4 py-2 font-mono-tiny bg-gold text-ink hover:bg-paper transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> {t.export}
            </button>
          </div>

          {/* Calibration progress panel */}
          <div className="mt-4 border border-paper/10 bg-paper2/5 rounded-sm overflow-hidden" data-testid="calibration-panel">
            <div className="px-4 py-3 border-b border-paper/10 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 font-mono-tiny text-gold">
                <ListChecks className="w-4 h-4" /> {t.progress}
              </div>
              <div className="font-mono-tiny text-paper/50">
                {editedCount} {t.completed} · {pendingPlayers.length} {t.filterPending}
              </div>
              <div className="ml-auto flex items-center gap-1 font-mono-tiny">
                <Filter className="w-3 h-3 text-paper/40 mr-1" />
                {[
                  { k: "all", label: t.filterAll },
                  { k: "pending", label: t.filterPending },
                  { k: "done", label: t.filterDone },
                ].map((f) => (
                  <button
                    key={f.k}
                    onClick={() => setPanelFilter(f.k)}
                    data-testid={`panel-filter-${f.k}`}
                    className={`px-2.5 py-1 border transition-colors ${
                      panelFilter === f.k
                        ? "bg-gold text-ink border-gold"
                        : "border-paper/20 text-paper/60 hover:border-gold hover:text-gold"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {["back", "middle", "front"].map((rowKey) => {
              const rowPlayers = filteredPanel.filter((p) => p.row === rowKey);
              if (rowPlayers.length === 0) return null;
              return (
                <div key={rowKey} className="px-4 py-3 border-b border-paper/10 last:border-b-0">
                  <div className="font-mono-tiny text-bordeaux mb-3 opacity-80">{rowLabel(rowKey)}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {rowPlayers.map((p) => {
                      const isDone = !!customCoords[p.id];
                      return (
                        <button
                          key={p.id}
                          onClick={() => focusPlayer(p.id)}
                          data-testid={`panel-player-${p.id}`}
                          className={`flex items-center gap-2 px-3 py-2 text-left border transition-colors group ${
                            isDone
                              ? "border-bordeaux/40 bg-bordeaux/10 hover:bg-bordeaux/20"
                              : "border-paper/15 hover:border-gold hover:bg-gold/5"
                          } ${focusedId === p.id ? "ring-1 ring-gold" : ""}`}
                        >
                          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? "bg-bordeaux" : "border border-paper/30"}`}>
                            {isDone && <Check className="w-2.5 h-2.5 text-paper" />}
                          </span>
                          <span className={`text-xs truncate flex-1 ${isDone ? "text-paper" : "text-paper/70 group-hover:text-gold"}`}>
                            {p.name}
                          </span>
                          <Target className="w-3 h-3 text-paper/30 group-hover:text-gold flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}

        {/* Legend */}
        <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono-tiny text-paper/50">
          <span>{effectivePlayers.length} {lang === "it" ? "figure identificate" : "identified figures"}</span>
          <span>{SIGNATURES.length} {lang === "it" ? "firme mappate" : "signatures mapped"}</span>
          <span className="ml-auto text-paper/40">{lang === "it" ? "Fonte statistiche: en.wikipedia · asroma.com · forza-roma" : "Stats source: en.wikipedia · asroma.com · forza-roma"}</span>
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[90] px-5 py-3 bg-gold text-ink font-mono-tiny shadow-lg"
            data-testid="edit-toast"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign-position modal in edit mode */}
      <AnimatePresence>
        {mode === "edit" && pendingClick && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[85] flex items-center justify-center p-4"
            onClick={() => setPendingClick(null)}
            data-testid="assign-modal-overlay"
          >
            <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-paper text-ink border border-gold/50 shadow-2xl"
              data-testid="assign-modal"
            >
              <div className="bg-bordeaux-deep text-paper px-6 py-4 flex items-center justify-between">
                <span className="font-mono-tiny text-gold">{t.assign}</span>
                <button onClick={() => setPendingClick(null)} className="text-paper/70 hover:text-gold" data-testid="assign-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-6">
                <div className="font-mono-tiny text-ink/50 mb-2">Coordinate</div>
                <div className="font-display text-3xl text-bordeaux mb-6">{pendingClick.x}%, {pendingClick.y}%</div>

                <label className="font-mono-tiny text-ink/50 block mb-2">{t.assignPick}</label>
                <select
                  value={pickedPlayer}
                  onChange={(e) => setPickedPlayer(e.target.value)}
                  data-testid="assign-select"
                  className="w-full border border-ink/20 bg-paper2 text-ink px-4 py-3 font-body focus:outline-none focus:border-bordeaux"
                >
                  <option value="">— {t.assignPick} —</option>
                  <optgroup label={lang === "it" ? "Fila in alto" : "Back row"}>
                    {PLAYERS.filter((p) => p.row === "back").map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label={lang === "it" ? "Fila centrale" : "Middle row"}>
                    {PLAYERS.filter((p) => p.row === "middle").map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label={lang === "it" ? "Fila seduta" : "Front row"}>
                    {PLAYERS.filter((p) => p.row === "front").map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                </select>

                <div className="mt-6 flex items-center gap-3">
                  <button
                    onClick={() => setPendingClick(null)}
                    data-testid="assign-cancel"
                    className="px-5 py-2.5 font-mono-tiny border border-ink/20 text-ink/70 hover:text-ink transition-colors"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={confirmAssign}
                    disabled={!pickedPlayer}
                    data-testid="assign-confirm"
                    className="flex items-center gap-2 px-5 py-2.5 font-mono-tiny bg-bordeaux text-paper hover:bg-bordeaux-deep transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" /> {t.confirm}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Card modal */}
      <AnimatePresence>
        {player && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
            onClick={() => setSelected(null)}
            data-testid="player-card-overlay"
          >
            <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl bg-paper text-ink shadow-2xl border border-gold/40"
              data-testid="player-card"
            >
              <div className="bg-bordeaux-deep text-paper px-6 md:px-10 py-5 flex items-center justify-between">
                <span className="font-mono-tiny text-gold">{t.season}</span>
                <div className="flex items-center gap-2">
                  <button onClick={goPrev} className="w-9 h-9 flex items-center justify-center border border-paper/20 text-paper/70 hover:text-gold hover:border-gold transition-colors" data-testid="player-card-prev" aria-label={t.prev}>
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={goNext} className="w-9 h-9 flex items-center justify-center border border-paper/20 text-paper/70 hover:text-gold hover:border-gold transition-colors" data-testid="player-card-next" aria-label={t.next}>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-paper/20 mx-1" />
                  <button onClick={() => setSelected(null)} className="w-9 h-9 flex items-center justify-center text-paper/70 hover:text-gold transition-colors" data-testid="player-card-close" aria-label={t.close}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="px-6 md:px-10 py-8 md:py-10">
                <div className="font-mono-tiny text-bordeaux mb-3">
                  {player.row === "back"
                    ? (lang === "it" ? "Fila in piedi · in alto" : "Standing · back row")
                    : player.row === "middle"
                    ? (lang === "it" ? "Fila in piedi · centro" : "Standing · middle row")
                    : (lang === "it" ? "Fila seduta" : "Seated row")}
                </div>
                <h3 className="font-display text-4xl md:text-6xl leading-[0.95] text-ink">{player.name}</h3>

                <div className="mt-6 grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="font-mono-tiny text-ink/40 mb-1">{t.role}</div>
                    <div className="font-display italic text-2xl md:text-3xl text-bordeaux">{player.role[lang]}</div>
                  </div>
                  <div>
                    <div className="font-mono-tiny text-ink/40 mb-1">{t.season}</div>
                    <div className="text-ink/90 text-base md:text-lg leading-snug">{player.stats[lang]}</div>
                  </div>
                </div>

                <div className="mt-8 h-px w-16 bg-gold" />

                <div className="mt-6">
                  <div className="font-mono-tiny text-ink/40 mb-2">{t.fact}</div>
                  <p className="text-ink/85 text-base md:text-lg leading-relaxed">{player.fact[lang]}</p>
                </div>

                <div className="mt-10 flex items-center justify-between font-mono-tiny">
                  <span className="text-ink/40">{String(selectedIndex + 1).padStart(2, "0")} / {String(effectivePlayers.length).padStart(2, "0")}</span>
                  <span className="text-ink/40 hidden sm:inline">{t.keyHint}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default InteractivePhoto;
